import Box from '@/common/components/Box';
import Button from '@/common/components/Button';
import { ChangeHistory } from '@/common/components/ChangeHistory';
import GoBackButton from '@/common/components/GoBackButton';
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder';
import { useGoBack } from '@/common/hooks/useGoBack';
import useModalMessagesSimple from '@/common/hooks/useModalMessagesSimple';
import { GeoTransformers } from '@/common/services/geoTransformers';
import { ErrorResponse } from '@/common/types/errorResponse';
import { FormTransformers } from '@/common/utils/formTransformers';
import { RequestedReview } from '@/features/establishmentLeadsFunnel/components/RequestedReview';
import { ReviewButtons } from '@/features/establishmentLeadsFunnel/components/ReviewButtons';
import { Checklist } from '@/features/privateCompanyLeadsFunnel/components/Checklist';
import { ContractEquipments } from '@/features/publicCompanyLeadsFunnel/components/ContractEquipments';
import { ContractFees } from '@/features/publicCompanyLeadsFunnel/components/ContractFees';
import { ContractInvoiceDetails } from '@/features/publicCompanyLeadsFunnel/components/ContractInvoiceDetails';
import { ContractProductCategories } from '@/features/publicCompanyLeadsFunnel/components/ContractProductCategories';
import {
  stateStrategy,
  translateStateStrategy,
} from '@/features/publicCompanyLeadsFunnel/constants/stateStrategy.constant';
import { useAdvancePublicCompanyLead } from '@/features/publicCompanyLeadsFunnel/hooks/useAdvancePublicCompanyLead';
import { useRegressPublicCompanyLead } from '@/features/publicCompanyLeadsFunnel/hooks/useRegressPublicCompanyLead';
import { useReviewContract } from '@/features/publicCompanyLeadsFunnel/hooks/useReviewContract';
import { useUpdatePublicCompanyLeadContract } from '@/features/publicCompanyLeadsFunnel/hooks/useUpdatePublicCompanyLeadContract';
import {
  PublicCompanyLeadsFunnelUpdateContractValidationSchema,
  updateContractSchema,
} from '@/features/publicCompanyLeadsFunnel/schemas/update.schema';
import { PublicCompanyLeadData } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { UserRoundIcon } from 'lucide-react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

const Form = ({ publicCompanyLead }: { publicCompanyLead: PublicCompanyLeadData; }) => {
  const { publicCompanyLeadId } = useParams();
  const contract = publicCompanyLead.contract;
  const isInQualifying = publicCompanyLead.state === 'qualifying';
  const isInContractingFinalization = publicCompanyLead.state === 'contracting_finalization';
  const isInCompleted = publicCompanyLead.state === 'completed';
  const passedEveryRequirement = publicCompanyLead?.requirement_list_summary?.steps?.every((step) => step.passed);

  const goBack = useGoBack();
  const queryClient = useQueryClient();

  const { setMessages } = useModalMessagesSimple({
    isError: true,
  });

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', publicCompanyLead.id] });
  };

  const onSuccessRedirect = () => {
    queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', publicCompanyLead.id] });
    goBack({ fallback: '/public-clients', searchLastRouteFromFallback: true });
  };

  const onError = (error: ErrorResponse) => {
    setMessages(error, { errorResponse: true });
  }

  const { mutate: advancePublicCompanyLead, isPending: advancePublicCompanyLeadIsPending } =
    useAdvancePublicCompanyLead({ id: publicCompanyLeadId, onSuccess: onSuccessRedirect, onError });

  const { mutate: regressLead } = useRegressPublicCompanyLead({
    id: publicCompanyLeadId,
    onSuccess: onSuccessRedirect,
    onError,
  });

  const { mutate: approveContract } = useReviewContract({
    publicCompanyLeadId,
    state: 'approved',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', publicCompanyLead.id] });
    },
    onError
  });

  const { mutate: declineContract } = useReviewContract({
    publicCompanyLeadId,
    state: 'disapproved',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', publicCompanyLead.id] });
    },
    onError,
  });

  const useFormMethods = useForm<PublicCompanyLeadsFunnelUpdateContractValidationSchema>({
    resolver: zodResolver(updateContractSchema),
    shouldFocusError: false,
    defaultValues: {
      contract: {
        geo_geoables: GeoTransformers.mapToGeo()(contract?.geo_geoables),
      },
    },
  });

  const { getValues, setError, trigger } = useFormMethods;

  const { mutate: updateContract } = useUpdatePublicCompanyLeadContract({
    publicCompanyLead,
    onSuccess,
    onError: FormTransformers.errorsTransformer<PublicCompanyLeadsFunnelUpdateContractValidationSchema>({ setMessages, setError }),
  });

  const onSubmit = (data: PublicCompanyLeadsFunnelUpdateContractValidationSchema) => {
    contract?.contract_equipment_categories?.forEach((category, index) => {
      if (category.id && data.contract.contract_equipment_categories?.[index]) {
        data.contract.contract_equipment_categories[index].id = category.id;
      }
    });

    updateContract(data);
  };

  const submit = async () => {
    const isValid = await trigger();

    if (!isValid)
      return;

    onSubmit(getValues());
  };

  const submittableOnBlur = (
    e: React.FocusEvent<HTMLInputElement, Element>,
    onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void,
  ) => {
    if (e.target.value === '') {
      return;
    }

    onBlur(e);
    submit();
  };

  return (
    <FormProvider {...useFormMethods}>
      <form>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback={`/public-clients/${publicCompanyLeadId}/edit`} />

            <p className="text-white-400 font-normal">{publicCompanyLead.display_name}</p>
          </div>
          {(isInQualifying || isInContractingFinalization) && (
            <div className="flex gap-4">
              <Button
                type="button"
                disabled={contract?.last_review?.state !== 'disapproved'}
                className={
                  contract?.last_review?.state === 'disapproved' ? 'shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]' : ''
                }
                variant={contract?.last_review?.state === 'disapproved' ? 'yellow-black' : 'yellow'}
                onClick={() => regressLead()}
              >
                Solicitar revisão
              </Button>
              <Button
                type="button"
                className={passedEveryRequirement ? 'shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]' : ''}
                variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                disabled={!passedEveryRequirement || advancePublicCompanyLeadIsPending}
                onClick={() => advancePublicCompanyLead()}
                isLoading={advancePublicCompanyLeadIsPending}
              >
                {isInQualifying ? 'Enviar para análise' : 'Iniciar operação'}
              </Button>
            </div>
          )}
        </div>
        <div className="flex gap-6 w-full">
          <Box className="flex w-full flex-col gap-4 h-[calc(100vh-128px)] overflow-y-auto shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
            {isInContractingFinalization && (
              <div className="flex justify-end">
                <ReviewButtons
                  reviewState={contract?.last_review?.state}
                  onApprove={approveContract}
                  onDecline={declineContract}
                />
              </div>
            )}
            {contract?.last_review?.state === 'disapproved' && (
              <RequestedReview description={contract?.last_review?.description} />
            )}
            <div className="flex gap-2 items-center">
              <GoBackButton dataTestid='contract_go_back' fallback={`/public-clients/${publicCompanyLeadId}/edit`} />

              {!isInCompleted ? (
                <div className="flex flex-col">
                  <p className="font-bold text-lg">
                    Condições comerciais <span className="font-light text-sm italic">{contract?.protocol_number}</span>
                  </p>
                </div>
              ) : (
                <div className="flex w-full items-center justify-between gap-2">
                  <p className="font-bold text-lg">Contrato</p>
                  <div className="flex flex-col items-end">
                    <div className="flex gap-2 items-center">
                      <ImageWithPlaceholder
                        url={publicCompanyLead?.creator?.profile_picture?.url}
                        alt={publicCompanyLead?.creator?.full_name}
                        className="size-6"
                        iconClassName="size-4"
                        placeholderIcon={UserRoundIcon}
                      />
                      <p className="font-bold text-xs">{publicCompanyLead?.creator?.full_name}</p>
                    </div>
                    <p className="font-light text-sm text-blue-800 italic">
                      Condição comercial - {contract?.protocol_number}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <ContractInvoiceDetails
              contract={contract}
              submittableOnBlur={submittableOnBlur}
              submit={submit}
              disableFields={!isInQualifying}
            />
            <ContractProductCategories
              lead={publicCompanyLead}
              submit={submit}
              disabled={!isInQualifying}
            />
            <ContractFees contract={contract} disabled={!isInQualifying} />
            <ContractEquipments contract={contract} disabled={!isInQualifying} />
          </Box>
          <div className="flex flex-col gap-4">
            <Checklist
              requirement_list_summary={publicCompanyLead.requirement_list_summary}
              state={translateStateStrategy[stateStrategy[publicCompanyLead.state as keyof typeof stateStrategy]]}
              className="flex flex-col gap-2 justify-between h-[calc((100vh-276px)/2)]"
              Buttons={() => (
                <>
                  {(isInQualifying || isInContractingFinalization) && (
                    <div className="flex flex-col gap-2">
                      <Button
                        type="button"
                        disabled={contract?.last_review?.state !== 'disapproved'}
                        variant={contract?.last_review?.state === 'disapproved' ? 'yellow-black' : 'yellow'}
                        onClick={() => regressLead()}
                      >
                        Solicitar revisão
                      </Button>
                      <Button
                        type="button"
                        variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                        disabled={!passedEveryRequirement || advancePublicCompanyLeadIsPending}
                        onClick={() => advancePublicCompanyLead()}
                        isLoading={advancePublicCompanyLeadIsPending}
                      >
                        {isInQualifying ? 'Enviar para análise' : 'Iniciar operação'}
                      </Button>
                    </div>
                  )}
                </>
              )}
            />
            <ChangeHistory logs={publicCompanyLead?.logs} entityId={publicCompanyLead.id} />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default Form;
